/**
 * Constantes del dominio de Estadísticas avanzadas: permiso de gating y las
 * opciones de período de cada gráfica (réplica de las pestañas del SSR).
 *
 * El gating de la UI usa el MISMO permiso que el menú del SSR y la página
 * `advanced_stats` (`advanced_stats`; ver `progress/explore_strangle_ssr.md` y
 * `navConfig.ts`). Los endpoints de analytics que consume la pantalla exigen
 * `view_orders` en el backend (`domains/stats.py`); en la práctica los roles con
 * `advanced_stats` también tienen `view_orders` (igual que la métrica avanzada del
 * dashboard, que ya pide `stats/effectiveness` bajo el gate `advanced_stats`).
 *
 * El orden y las etiquetas de los períodos replican EXACTAMENTE los del SSR
 * (`templates/advanced_stats_with_charts.html`), para no inventar una nomenclatura
 * distinta a la que el usuario conoce.
 */
import type { OrdersPeriod, RevenuePeriod, SellerPeriod } from "./schemas";

/** Permiso que gobierna las estadísticas avanzadas (igual que el menú y el SSR). */
export const ADVANCED_STATS_PERMISSION = "advanced_stats";

/** Una opción de un selector de período (valor de la API + etiqueta del SSR). */
export interface PeriodOption<T extends string> {
  value: T;
  label: string;
}

/**
 * Períodos del panel global y de la gráfica de órdenes ("Hoy / Semana / Mes" del
 * SSR), en su orden. El panel global usa la etiqueta corta de las pestañas
 * superiores; la gráfica de órdenes, las mini-pestañas "Día / Sem. / Mes".
 */
export const ORDERS_PERIOD_OPTIONS: PeriodOption<OrdersPeriod>[] = [
  { value: "day", label: "Hoy" },
  { value: "week", label: "Semana" },
  { value: "month", label: "Mes" },
];

/** Mini-pestañas de la gráfica de órdenes ("Día / Sem. / Mes"). */
export const ORDERS_CHART_PERIOD_OPTIONS: PeriodOption<OrdersPeriod>[] = [
  { value: "day", label: "Día" },
  { value: "week", label: "Sem." },
  { value: "month", label: "Mes" },
];

/** Período por defecto del panel global (igual que el SSR: "Hoy"). */
export const DEFAULT_ORDERS_PERIOD: OrdersPeriod = "day";

/** Mini-pestañas de la gráfica de ingresos ("Sem. / Mes / Año"). */
export const REVENUE_PERIOD_OPTIONS: PeriodOption<RevenuePeriod>[] = [
  { value: "week", label: "Sem." },
  { value: "month", label: "Mes" },
  { value: "year", label: "Año" },
];

/** Período por defecto de la gráfica de ingresos (igual que el SSR: "Sem."). */
export const DEFAULT_REVENUE_PERIOD: RevenuePeriod = "week";

/** Mini-pestañas de ventas por vendedor ("Día / Sem. / Mes / Año"). */
export const SELLER_PERIOD_OPTIONS: PeriodOption<SellerPeriod>[] = [
  { value: "day", label: "Día" },
  { value: "week", label: "Sem." },
  { value: "month", label: "Mes" },
  { value: "year", label: "Año" },
];

/** Período por defecto de ventas por vendedor (igual que el SSR: "Sem."). */
export const DEFAULT_SELLER_PERIOD: SellerPeriod = "week";

/** Mini-pestañas de pedidos de láminas recibidos ("Día / Sem. / Mes"). */
export const PEDIDOS_PERIOD_OPTIONS: PeriodOption<OrdersPeriod>[] = [
  { value: "day", label: "Día" },
  { value: "week", label: "Sem." },
  { value: "month", label: "Mes" },
];

/** Período por defecto de pedidos de láminas (igual que el SSR: "Día"). */
export const DEFAULT_PEDIDOS_PERIOD: OrdersPeriod = "day";

/** Mensaje genérico ante un error inesperado (no-API). */
export const STATS_GENERIC_ERROR = "Ocurrió un error inesperado.";
